/**
 * m3 echo Frontend - Mandanten-Dialog
 *
 * Dialog zum Erstellen oder Bearbeiten eines Mandanten.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Tenant, TenantCreate, TenantUpdate, TenantModelStatus, tenantApi } from '../../services/admin';
import { TenantEffectiveConfig, billingApi } from '../../services/billing';
import SSOConfigSection from './SSOConfigSection';

// Microsoft Entra ID SSO is temporarily disabled. Flip to true and also set
// SSO_ENABLED=True in backend/app/api/sso.py to bring the feature back.
const SSO_ENABLED = false;

interface TenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: TenantCreate | TenantUpdate) => Promise<void>;
  tenant?: Tenant | null;
  mode: 'create' | 'edit';
}

export const TenantDialog: React.FC<TenantDialogProps> = ({
  open,
  onClose,
  onSave,
  tenant,
  mode,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<TenantCreate & { show_benchmark_tab?: boolean; monthly_budget?: number | null }>({
    name: '',
    display_name: '',
    is_active: true,
    show_benchmark_tab: true,
    monthly_budget: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelStatuses, setModelStatuses] = useState<TenantModelStatus[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [effectiveConfig, setEffectiveConfig] = useState<TenantEffectiveConfig | null>(null);
  const [, setHasAnyModules] = useState(false);

  // Load tenant data when editing
  useEffect(() => {
    if (mode === 'edit' && tenant) {
      setFormData({
        name: tenant.name,
        display_name: tenant.display_name,
        is_active: tenant.is_active,
        show_benchmark_tab: tenant.show_benchmark_tab,
        monthly_budget: (tenant as any).monthly_budget ?? null,
      });
      // Load model statuses and billing effective config for the tenant
      loadModelStatuses(tenant.id);
      loadEffectiveConfig(tenant.id);
    } else {
      setFormData({
        name: '',
        display_name: '',
        is_active: true,
        show_benchmark_tab: true,
      });
      setModelStatuses([]);
      setEffectiveConfig(null);
      setHasAnyModules(false);
    }
    setError(null);
  }, [mode, tenant, open]);

  const loadModelStatuses = async (tenantId: number) => {
    setLoadingModels(true);
    try {
      const response = await tenantApi.getModelStatus(tenantId);
      setModelStatuses(response.data);
    } catch (err) {
      console.error('Error loading model statuses:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  const loadEffectiveConfig = async (tenantId: number) => {
    try {
      const response = await billingApi.getEffectiveConfig(tenantId);
      setEffectiveConfig(response.data);
      // Check if tenant has any module assignments at all
      const modulesResponse = await billingApi.getTenantModules(tenantId);
      setHasAnyModules(modulesResponse.data.length > 0);
    } catch (err) {
      console.error('Error loading effective config:', err);
      setEffectiveConfig(null);
      setHasAnyModules(false);
    }
  };

  const handleModelToggle = (modelName: string) => {
    setModelStatuses((prev) =>
      prev.map((model) =>
        model.model_name === modelName
          ? { ...model, is_active: !model.is_active }
          : model
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'create') {
        await onSave(formData);
      } else {
        // For edit mode, only send changed fields
        const updateData: TenantUpdate & { monthly_budget?: number | null } = {
          display_name: formData.display_name,
          is_active: formData.is_active,
          show_benchmark_tab: formData.show_benchmark_tab,
          monthly_budget: formData.monthly_budget,
        };
        await onSave(updateData);

        // Save model statuses if we have any
        if (tenant && modelStatuses.length > 0) {
          await tenantApi.updateModelStatus(tenant.id, {
            model_statuses: modelStatuses.map((m) => ({
              model_name: m.model_name,
              is_active: m.is_active,
            })),
          });
        }
      }
      onClose();
    } catch (err: any) {
      console.error('Fehler beim Speichern des Mandanten:', err);
      setError(err.message || 'Fehler beim Speichern des Mandanten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === 'create' ? 'Mandant Erstellen' : 'Mandant Bearbeiten'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Mandantenname - nur beim Erstellen bearbeitbar */}
            <TextField
              label="Mandantenname"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              disabled={mode === 'edit'}
              helperText={
                mode === 'create'
                  ? 'Eindeutiger Identifier für den Mandanten (Kleinbuchstaben, keine Leerzeichen)'
                  : 'Mandantenname kann nicht geändert werden'
              }
              fullWidth
            />

            {/* Anzeigename */}
            <TextField
              label="Anzeigename"
              value={formData.display_name}
              onChange={(e) =>
                setFormData({ ...formData, display_name: e.target.value })
              }
              required
              helperText="Lesbarer Name für den Mandanten"
              fullWidth
            />

            {/* Aktiv-Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              }
              label="Aktiv"
            />

            {/* Benchmark Funktion */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_benchmark_tab}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        show_benchmark_tab: e.target.checked,
                      });
                    }}
                  />
                }
                label="Optimierungsfunktion"
                sx={{ mr: 0 }}
              />
              {mode === 'edit' && effectiveConfig && !effectiveConfig.active_features['show_benchmark_tab'] && (
                <Chip
                  label="Modul fehlt"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>

            {/* Manueller LLM Score: kein Toggle mehr — wird ausschließlich über das
                Billing-Modul „Manueller LLM Score" freigeschaltet. Verwaltung im Billing-Tab. */}

            {/* Tagesquota (read-only). Basis ist fest auf 10 für alle Mandanten;
                Erhöhungen ausschließlich über Billing-Module. */}
            {mode === 'edit' && (
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                  Tagesquota Prompt-Ausführungen
                </Typography>
                <Typography variant="body2">
                  10 Basis + {effectiveConfig?.effective_quota ?? 0} aus Modulen ={' '}
                  <strong>{10 + (effectiveConfig?.effective_quota ?? 0)} Prompts/Tag</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Die Basis-Quota ist für alle Mandanten fest auf 10 gesetzt. Zusätzliches
                  Kontingent wird über den Billing-Tab als Modul gebucht. Reset um Mitternacht
                  in Mandanten-Zeitzone.
                </Typography>
              </Box>
            )}

            <TextField
              label="Monatsbudget (EUR)"
              type="number"
              value={formData.monthly_budget ?? ''}
              onChange={(e) =>
                setFormData({ ...formData, monthly_budget: e.target.value ? parseFloat(e.target.value) : null })
              }
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Maximales API-Kostenbudget pro Monat. Leer = kein Limit."
              fullWidth
            />

            {/* KI-Modelle - only show when editing */}
            {mode === 'edit' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Verfügbare KI-Modelle
                </Typography>
                {loadingModels ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : modelStatuses.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {modelStatuses.map((model) => (
                      <Box
                        key={model.model_name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={model.is_active}
                                onChange={() => handleModelToggle(model.model_name)}
                                size="small"
                              />
                            }
                            label={model.model_name}
                            sx={{ mr: 0 }}
                          />
                          {!model.is_globally_active && (
                            <Chip
                              label="Global deaktiviert"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {effectiveConfig && !effectiveConfig.active_models.includes(model.model_name) && (
                            <Chip
                              label="Modul fehlt"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Keine Modelle verfügbar
                  </Typography>
                )}
              </>
            )}

            {/* SSO Configuration - only show when editing.
                Microsoft Entra ID SSO is temporarily disabled (see SSO_ENABLED in
                backend/app/api/sso.py and frontend/src/pages/Login.tsx). */}
            {SSO_ENABLED && mode === 'edit' && tenant && (
              <SSOConfigSection tenantId={tenant.id} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {mode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TenantDialog;
